/**
 * ADR-0243 — one card per tracked visitor under the People chip (founder
 * 2026-10-14: "list one card per visitor, like bots").
 *
 *   🇺🇸 Visitor from Pembroke Pines                     2m ago
 *      Chrome on Windows · 6 visits · first seen Sep 14
 *
 * Built only from the coarse location and the closed device labels the
 * people list returns. Never an IP: addresses live in the timeline only,
 * under the existing role rules.
 */

import type { PersonSummary } from '@sill/types/api';

import { RelativeTime } from '../../components/atoms/index.js';
import { CountryFlag } from '../../components/atoms/CountryFlag.js';
import { useDisplayTimeZone } from '../../app/time-zone-context.js';
import { useC } from '../../lib/theme.js';
import { deviceLabel, personName } from '../people/person-label.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/** "Sep 14" (same year) / "Sep 14, 2025", in the display time zone. */
export function firstSeenLabel(iso: string, timeZone: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const year = (x: Date): string => new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric' }).format(x);
  const sameYear = year(d) === year(now);
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d);
}

/** Line 2: "Chrome on Windows · 6 visits · first seen Sep 14". */
export function personSecondLine(p: PersonSummary, timeZone: string, now?: Date): string {
  const parts: string[] = [];
  const dev = deviceLabel(p.device);
  if (dev !== null) parts.push(dev);
  parts.push(`${p.visits.toLocaleString('en-US')} visit${p.visits === 1 ? '' : 's'}`);
  const first = firstSeenLabel(p.first_seen, timeZone, now);
  if (first.length > 0) parts.push(`first seen ${first}`);
  return parts.join(' · ');
}

export interface PersonRowProps {
  readonly person: PersonSummary;
  readonly isSelected: boolean;
  readonly isLast: boolean;
  readonly onSelect: () => void;
}

export function PersonRow({ person, isSelected, isLast, onSelect }: PersonRowProps): JSX.Element {
  const C = useC();
  const tz = useDisplayTimeZone();
  const country = person.location?.country ?? null;
  return (
    <button
      type="button"
      data-person-id={person.person_id}
      data-group-key={person.group_key}
      data-group-kind="person"
      onClick={onSelect}
      aria-pressed={isSelected}
      className="w-full p-4 text-left"
      style={{
        cursor: 'pointer',
        background: isSelected ? C.surface2 : 'transparent',
        borderLeft: `2px solid ${isSelected ? C.limeFg : 'transparent'}`,
        borderBottom: !isLast ? `1px solid ${C.borderSoft}` : 'none',
        borderTop: 'none',
        borderRight: 'none',
        transition: 'background-color 800ms ease-out, border-left-color 800ms ease-out',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }} data-testid="person-flag">
            {country !== null ? <CountryFlag code={country} size={16} /> : null}
          </span>
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600, overflowWrap: 'anywhere' }}>
            {personName(person)}
          </span>
        </div>
        <RelativeTime iso={person.last_seen} style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, flexShrink: 0 }} />
      </div>
      <div
        data-testid="person-line2"
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          marginTop: 4,
          paddingLeft: 24,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {personSecondLine(person, tz)}
      </div>
    </button>
  );
}
